import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createBlog, updateBlog, getBlogById, uploadBlogImage } from '../../services/blogService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faItalic, faUnderline, faLink, faHeading } from '@fortawesome/free-solid-svg-icons';

const BlogEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);
  
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    body: '',
    featuredImage: '',
    tags: '',
    published: true
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const bodyTextareaRef = useRef(null);

  useEffect(() => {
    if (isEditing) {
      fetchBlog();
    }
  }, [id, isEditing]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const blog = await getBlogById(id);
      
      if (!blog) {
        setError('Blog not found');
        return;
      }
      
      setFormData({
        title: blog.title || '',
        author: blog.author || '',
        body: blog.body || '',
        featuredImage: blog.featuredImage || '',
        tags: Array.isArray(blog.tags) ? blog.tags.join(', ') : '',
        published: blog.published || false
      });
    } catch (err) {
      console.error('Error fetching blog:', err);
      setError('Failed to load blog');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setImageUploading(true);
      const imageUrl = await uploadBlogImage(file);
      setFormData(prev => ({
        ...prev,
        featuredImage: imageUrl
      }));
      setImageFile(null);
    } catch (err) {
      console.error('Error uploading image:', err);
      alert(err.message || 'Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!formData.body.trim()) {
      setError('Content is required');
      return;
    }

    try {
      setSaving(true);
      setError('');
      
      const blogData = {
        title: formData.title.trim(),
        author: formData.author.trim() || 'TrendingChess Team',
        body: formData.body.trim(),
        featuredImage: formData.featuredImage.trim(),
        tags: formData.tags
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag.length > 0),
        published: formData.published
      };

      if (isEditing) {
        await updateBlog(id, blogData);
      } else {
        await createBlog(blogData);
      }
      
      navigate('/admin');
    } catch (err) {
      console.error('Error saving blog:', err);
      setError(err.message || 'Failed to save blog');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? Any unsaved changes will be lost.')) {
      navigate('/admin');
    }
  };

  const insertTextAtCursor = (beforeText, afterText = '') => {
    const textarea = bodyTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.body.substring(start, end);
    const newText = beforeText + selectedText + afterText;
    
    const newBody = formData.body.substring(0, start) + newText + formData.body.substring(end);
    
    setFormData(prev => ({ ...prev, body: newBody }));
    
    // Restore cursor position
    setTimeout(() => {
      const newCursorPos = start + beforeText.length + selectedText.length + afterText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
      textarea.focus();
    }, 0);
  };

  const handleFormat = (type) => {
    switch (type) {
      case 'bold':
        insertTextAtCursor('**', '**');
        break;
      case 'italic':
        insertTextAtCursor('***', '***');
        break;
      case 'underline':
        insertTextAtCursor('__', '__');
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) {
          insertTextAtCursor(`*${url}*`);
        }
        break;
      case 'heading':
        insertTextAtCursor('# ');
        break;
      case 'subheading':
        insertTextAtCursor('## ');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading blog...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-100">
            {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
          </h2>
          <p className="text-gray-400 mt-2">
            {isEditing ? 'Update your blog content' : 'Share insights with the chess community'}
          </p>
        </div>
        <button
          onClick={handleCancel}
          className="text-gray-400 hover:text-gray-300 px-4 py-2 rounded-lg transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Title */}
          <div className="md:col-span-2">
            <label htmlFor="title" className="block text-gray-300 mb-2 font-medium">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Enter blog post title"
              required
            />
          </div>

          {/* Author */}
          <div>
            <label htmlFor="author" className="block text-gray-300 mb-2 font-medium">
              Author
            </label>
            <input
              type="text"
              id="author"
              name="author"
              value={formData.author}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="Author name (optional)"
            />
          </div>

          {/* Tags */}
          <div>
            <label htmlFor="tags" className="block text-gray-300 mb-2 font-medium">
              Tags
            </label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              placeholder="tag1, tag2, tag3"
            />
            <p className="text-gray-500 text-sm mt-1">Separate tags with commas</p>
          </div>
        </div>

        {/* Featured Image */}
        <div>
          <label className="block text-gray-300 mb-2 font-medium">
            Featured Image
          </label>
          
          <div className="space-y-4">
            {/* Current Image Preview */}
            {formData.featuredImage && (
              <div className="relative">
                <img 
                  src={formData.featuredImage} 
                  alt="Featured"
                  className="w-full h-48 object-cover rounded-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, featuredImage: '' }))}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors"
                  title="Remove image"
                >
                  ×
                </button>
              </div>
            )}
            
            {/* Image Upload */}
            <div className="flex gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={imageUploading}
                className="hidden"
                id="image-upload"
              />
              <label
                htmlFor="image-upload"
                className={`bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                  imageUploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {imageUploading ? 'Uploading...' : 'Upload Image'}
              </label>
              
              {/* Manual URL Input */}
              <input
                type="url"
                value={formData.featuredImage}
                onChange={(e) => setFormData(prev => ({ ...prev, featuredImage: e.target.value }))}
                className="flex-1 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Or paste image URL"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div>
          <label htmlFor="body" className="block text-gray-300 mb-2 font-medium">
            Content *
          </label>
          
          {/* Formatting Toolbar */}
          <div className="bg-gray-800 border border-gray-600 rounded-t-lg px-3 py-2 flex gap-2">
            <button
              type="button"
              onClick={() => handleFormat('bold')}
              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              title="Bold (**text**)"
            >
              <FontAwesomeIcon icon={faBold} />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('italic')}
              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              title="Italic (***text***)"
            >
              <FontAwesomeIcon icon={faItalic} />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('underline')}
              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              title="Underline (__text__)"
            >
              <FontAwesomeIcon icon={faUnderline} />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('link')}
              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              title="Link (*url*)"
            >
              <FontAwesomeIcon icon={faLink} />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('heading')}
              className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors"
              title="Heading (# text)"
            >
              <FontAwesomeIcon icon={faHeading} />
            </button>
            <button
              type="button"
              onClick={() => handleFormat('subheading')}
              className="px-3 py-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded transition-colors text-sm font-bold"
              title="Subheading (## text)"
            >
              H2
            </button>
          </div>
          
          <textarea
            ref={bodyTextareaRef}
            id="body"
            name="body"
            value={formData.body}
            onChange={handleInputChange}
            rows={20}
            className="w-full px-4 py-3 bg-gray-700 border border-gray-600 border-t-0 rounded-b-lg text-gray-100 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-mono text-sm"
            placeholder="Write your blog content here...

Use the toolbar above for formatting:
**bold text** ***italic*** __underline__
*url* for links
# Heading
## Subheading

Line breaks will be preserved."
            required
          />
          <p className="text-gray-500 text-sm mt-2">
            Use toolbar for formatting or type: **bold**, ***italic***, __underline__, *url*, # Heading, ## Subheading
          </p>
        </div>

        {/* Published Toggle */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="published"
            name="published"
            checked={formData.published}
            onChange={handleInputChange}
            className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
          />
          <label htmlFor="published" className="text-gray-300 font-medium">
            Publish immediately
          </label>
          <span className="text-gray-500 text-sm">
            (Uncheck to save as draft)
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-4 pt-6 border-t border-gray-700">
          <button
            type="submit"
            disabled={saving}
            className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg transition-colors font-medium"
          >
            {saving ? 'Saving...' : (isEditing ? 'Update Post' : 'Create Post')}
          </button>
          
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="bg-gray-600 hover:bg-gray-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogEditor;